'use client';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { CONTACT_CONFIG } from '@eliadmin/core/src/constants/contact-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { trackFormSubmitSuccess } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';

import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { Heading } from '../ui/heading';
import { Paragraph } from '../ui/paragraph';
import { Field } from '../form/labeled-field';
import { fetchDynamicSectionsById, type TDynamicData } from '@eliadmin/core/src/api/dynamics/dynamics-api';
import { ButtonIcon } from '../ui/button-icon';
import Link from '@eliadmin/core/src/components/link';

const contactSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    custom: z.object({
      interest: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    }),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    // The phone field is rendered as required; without it in the schema zod stripped the value
    // and the number never reached the shop. min(12): the phone input pre-fills the dial code
    // ("+421"), same rule as in the checkout schema.
    phone: z.string().min(12, VM.CANT_BE_EMPTY_PHONE_VALIDATION_MESSAGE()),
    text: z.string(),
    gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    attachments: z.array(z.object({ id: z.number() })).optional(),
    'g-recaptcha-response': z.string().default(''),
  });

export type ContactFormValues = z.infer<ReturnType<typeof contactSchema>>;

export function ContactForm() {
  const { t } = useTranslations();
  const generalLayout = useGlobalStore(s => s.generalLayout);
  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      custom: {
        interest: '',
      },
      email: '',
      phone: '',
      text: '',
      gdpr: false,
      attachments: [],
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: sendContactMessage,
  });

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {

      await mutateAsync({
        data: {
          ...data,

        },
        locale,
      });
      trackFormSubmitSuccess({
        form_id: 'contact',
        form_name: 'contact_form',
        form_destination: typeof window !== 'undefined' ? window.location.href : undefined,
        form_length: Object.keys(data).length,
        send_to: generalLayout.marketing?.gtm_id,
      });
      toast({
        title: t('text658'),
        variant: 'success',
      });
      router.push(ROUTES.HOME());
    } catch (err) {
      // handle 422
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text657'),
        description: (err as any)?.message,
        variant: 'destructive',
      });
    }
  });

  const { data: interests = [] } = useQuery({
    queryKey: ['INTERESTS', locale],
    queryFn: () => fetchDynamicSectionsById<TDynamicData>({ locale, id: 3 }),
  });

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className={cn(
          'flex w-full flex-col items-start justify-start gap-6 rounded-[12px] bg-white p-4 sm:gap-8 sm:p-6 lg:p-8',
          'ea-contact-form',
        )}
      >
        <div className="flex w-full flex-col items-start justify-start gap-3">
          <Heading title={t('text82')} tag="h3" />
          <Paragraph color="muted-foreground">{t('text3351')}</Paragraph>
        </div>
        <div className="grid w-full grid-cols-2 gap-4">
          <Field
            required
            control={form?.control as any}
            name="name"
            label="Meno/názov spoločnosti"
            placeholder="Meno/názov spoločnosti"
            className={{ wrapper: 'col-span-2 w-full' }}
          />
          <Field
            required
            control={form?.control as any}
            name="email"
            label="E-mail"
            type="email"
            placeholder="E-mail"
            className={{ wrapper: 'col-span-2 w-full sm:col-span-1' }}
            before={<ButtonIcon alt={t('text185')} url="/images/icons/form/email.svg" />}
          />
          <Field
            required
            control={form?.control as any}
            name="phone"
            type="tel"
            label={t('text1258')}
            placeholder={t('text1258')}
            className={{ wrapper: 'col-span-2 w-full sm:col-span-1' }}
          />
          <Field
            required
            control={form?.control as any}
            name="custom.interest"
            type="select"
            label={t('text3714')}
            options={interests.map(interest => ({
              value: interest.name,
              label: interest.name,
            }))}
            placeholder="Vyberte"
            className={{ wrapper: 'col-span-2 w-full' }}
          />
          <Field
            control={form?.control as any}
            name="text"
            type="textarea"
            label={t('text2340')}
            placeholder={t('text2340')}
            className={{ wrapper: 'col-span-2 w-full' }}
          />
        </div>
        <FormField
          control={form.control}
          name="gdpr"
          render={({ field }) => (
            <FormItem
              className={cn(
                'col-span-12 flex flex-wrap items-start',
                'ea-contact-form__gdpr-checkbox-item ea-contact-form__checkbox-item',
              )}
            >
              <FormControl>
                <Checkbox
                  ref={field.ref}
                  name={field.name}
                  disabled={field.disabled}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  className={cn('', 'ea-contact-form__gdpr-checkbox ea-contact-form__checkbox')}
                />
              </FormControl>
              <FormLabel className={cn('ea-contact-form__gdpr-checkbox-label')}>
                <Paragraph size="lg" className="w-full">
                  {t('text613')}{' '}
                  <Link
                    href="/ochrana-osobnych-udajov"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="generic-hover font-semibold text-primary"
                  >
                    {t('text614')}
                  </Link>{' '}
                  {t('text3354')}
                </Paragraph>
              </FormLabel>
              <FormMessage className="w-full pt-1" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'g-recaptcha-response'}
          render={() => (
            // `hidden` also hid server-side captcha errors; hide only while empty.
            <FormItem required className={cn('col-span-12', 'empty:hidden')}>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          variant={CONTACT_CONFIG.form.submit.variant}
          className={cn('col-span-12', 'ea-contact-form__submit')}
          isLoading={form.formState.isSubmitting}
        >
          <ButtonIcon alt={t('text185')} url="/images/icons/form/send.svg" />
          {t('text185')}
        </Button>
      </form>
    </Form>
  );
}
